import { Icon } from '@natera/material/lib/icon'
import React from 'react'
import { cn } from '~/shared/lib/cn'
import { highlightText } from '~/shared/lib/highlight-text'
import { Text } from '~/shared/ui'
import type { AuditTrailEventDescriptionField } from '../../model'

function FieldRow({
  field,
  searchQuery,
  indent,
}: {
  field: AuditTrailEventDescriptionField
  searchQuery: string
  indent?: boolean
}) {
  return (
    <>
      <div className="flex gap-1">
        {indent && (
          <Icon
            className="text-text-tertiary -scale-x-100 text-[20px]"
            children="keyboard_return"
          />
        )}
        <Text
          className={cn('text-text-secondary')}
          appearance="BODY2"
        >
          {highlightText(field.label, searchQuery)}
        </Text>
      </div>
      <Text
        className="flex min-w-0 flex-col"
        appearance="BODY2"
      >
        {field.values.map((value) => {
          const action = value.action ?? field.action
          return (
            <span key={`${value.current}-${value.previous}-${value.diff}-${action}`}>
              {value.previous && value.current && (
                <>
                  <span className="text-text-tertiary line-through">
                    {highlightText(value.previous, searchQuery)}
                  </span>{' '}
                  → {highlightText(value.current, searchQuery)}
                </>
              )}
              {value.diff && value.diff.length > 0 && (
                <span className="flex flex-col gap-0.5">
                  {value.diff.map((val) => (
                    <div
                      key={val}
                      className="flex gap-1"
                    >
                      {action === 'added' && (
                        <Icon
                          className="text-action-active px-1 text-[18px]"
                          children="add"
                        />
                      )}
                      {action === 'removed' && (
                        <Icon
                          className="text-action-active px-1 text-[18px]"
                          children="delete"
                        />
                      )}
                      {action === 'suggested' && (
                        <Icon
                          className="text-action-active px-1 text-[18px]"
                          children="wb_incandescent"
                        />
                      )}
                      <span className="wrap-break-word">{highlightText(val, searchQuery)}</span>
                    </div>
                  ))}
                </span>
              )}
            </span>
          )
        })}
      </Text>
    </>
  )
}

interface FieldsGridProps {
  className?: string
  fields: AuditTrailEventDescriptionField[]
  searchQuery: string
  userRole: 'person' | 'opti'
  /** Override the default data column header */
  dataLabel?: string
}

/** 2-column label/value grid with field diff rendering (previous->current, add/remove/suggest icons). */
export function FieldsGrid({
  className,
  fields,
  searchQuery,
  userRole,
  dataLabel,
}: FieldsGridProps) {
  const header = dataLabel ?? (userRole === 'opti' ? 'Extracted Data' : 'Validated Data')

  return (
    <div className={cn('mt-2 grid grid-cols-[150px_1fr] gap-x-2 gap-y-0.5', className)}>
      <Text appearance="OVERLINE">Field Name</Text>
      <Text appearance="OVERLINE">{header}</Text>
      {fields.map((field) => (
        <React.Fragment key={field.label}>
          <FieldRow
            field={field}
            searchQuery={searchQuery}
          />
          {field.subFields?.map((sub) => (
            <FieldRow
              key={sub.label + (sub.action ?? '')}
              field={sub}
              searchQuery={searchQuery}
              indent
            />
          ))}
        </React.Fragment>
      ))}
    </div>
  )
}
